import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Card,
  Button,
  TableContainer,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = (apiHost() || "").replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const formatRupiah = (n) => {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(n) || 0);
};

const PERIODE_OPTIONS = [
  "januari", "februari", "maret", "april", "mei", "juni",
  "juli", "agustus", "september", "oktober", "november", "desember",
];

const MengelolaGajiKaryawan = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [rekap, setRekap] = useState({ rekap: [], totalSemua: 0, tahun: new Date().getFullYear() });
  const [loading, setLoading] = useState(true);
  const [rekapLoading, setRekapLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [periode, setPeriode] = useState("");
  const [status, setStatus] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const fetchGaji = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (search) params.set("search", search);
      if (periode) params.set("periode", periode);
      if (status) params.set("status", status);
      const res = await axios.get(`${getApiBaseUrl()}/gaji?${params}`, { withCredentials: true });
      setList(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (e) {
      setSnackbar({ open: true, message: "Gagal memuat data gaji", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchRekap = async () => {
    setRekapLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/gaji/rekap?tahun=${rekap.tahun}`, { withCredentials: true });
      setRekap(res.data?.data || { rekap: [], totalSemua: 0, tahun: rekap.tahun });
    } catch (e) {
      setSnackbar({ open: true, message: "Gagal memuat rekap", severity: "error" });
    } finally {
      setRekapLoading(false);
    }
  };

  useEffect(() => {
    fetchGaji();
  }, [page, search, periode, status]);

  useEffect(() => {
    fetchRekap();
  }, [rekap.tahun]);

  const handleEdit = (row) => {
    setEditRow(row);
    setEditForm({
      tanggal_periode: row.tanggal_periode || "",
      periode: row.periode,
      nominal: row.nominal,
      tambahan: row.tambahan || 0,
      potongan: row.potongan || 0,
      keterangan: row.keterangan || "",
      status: row.status,
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editRow) return;
    try {
      await axios.put(`${getApiBaseUrl()}/gaji/${editRow.uuid}`, editForm, { withCredentials: true });
      setSnackbar({ open: true, message: "Gaji berhasil diubah", severity: "success" });
      setEditOpen(false);
      fetchGaji();
      fetchRekap();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Gagal mengubah", severity: "error" });
    }
  };

  const handleDelete = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: "Hapus gaji?",
      text: "Data tidak dapat dikembalikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    try {
      await axios.delete(`${getApiBaseUrl()}/gaji/${row.uuid}`, { withCredentials: true });
      setSnackbar({ open: true, message: "Gaji berhasil dihapus", severity: "success" });
      fetchGaji();
      fetchRekap();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Gagal menghapus", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Mengelola Gaji Karyawan
      </Typography>

      {/* Rekap */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Rekap Gaji
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <TextField
            type="number"
            size="small"
            label="Tahun"
            value={rekap.tahun}
            onChange={(e) => setRekap((r) => ({ ...r, tahun: Number(e.target.value) || new Date().getFullYear() }))}
            sx={{ width: 100 }}
          />
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchRekap} disabled={rekapLoading}>
            Refresh
          </Button>
        </Box>
        {rekapLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography color="text.secondary">Total Semua (tahun {rekap.tahun})</Typography>
              <Typography variant="h6">{formatRupiah(rekap.totalSemua)}</Typography>
            </Grid>
            {rekap.rekap?.slice(0, 6).map((r, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Typography color="text.secondary">{r.periode}</Typography>
                <Typography variant="body2">{formatRupiah(r.total)} ({r.count} slip)</Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </Card>

      <Card sx={{ p: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Cari username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Periode</InputLabel>
            <Select value={periode} label="Periode" onChange={(e) => setPeriode(e.target.value)}>
              <MenuItem value="">Semua</MenuItem>
              {PERIODE_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">Semua</MenuItem>
              <MenuItem value="menunggu">Menunggu</MenuItem>
              <MenuItem value="diterima">Diterima</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/gaji-karyawan/create")}>
            Tambah Gaji
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Karyawan</TableCell>
                <TableCell>Cabang</TableCell>
                <TableCell>Periode</TableCell>
                <TableCell>Tanggal</TableCell>
                <TableCell align="right">Nominal</TableCell>
                <TableCell align="right">Jumlah</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
              ) : list.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">Tidak ada data</TableCell></TableRow>
              ) : (
                list.map((row) => (
                  <TableRow key={row.uuid}>
                    <TableCell>{row.User?.username}</TableCell>
                    <TableCell>{row.User?.Cabang?.namacabang || "-"}</TableCell>
                    <TableCell>{row.periode}</TableCell>
                    <TableCell>{row.tanggal_periode ? new Date(row.tanggal_periode).toLocaleDateString("id-ID") : "-"}</TableCell>
                    <TableCell align="right">{formatRupiah(row.nominal)}</TableCell>
                    <TableCell align="right">{formatRupiah(row.jumlah)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={row.status === "diterima" ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(row)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
            <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
            <Typography sx={{ alignSelf: "center" }}>Halaman {page} / {totalPages}</Typography>
            <Button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Selanjutnya</Button>
          </Box>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Gaji</DialogTitle>
        <DialogContent>
          {editRow && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Tanggal Periode"
                  value={editForm.tanggal_periode || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, tanggal_periode: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Periode</InputLabel>
                  <Select
                    value={editForm.periode}
                    label="Periode"
                    onChange={(e) => setEditForm((f) => ({ ...f, periode: e.target.value }))}
                  >
                    {PERIODE_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Nominal"
                  value={editForm.nominal}
                  onChange={(e) => setEditForm((f) => ({ ...f, nominal: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tambahan"
                  value={editForm.tambahan}
                  onChange={(e) => setEditForm((f) => ({ ...f, tambahan: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Potongan"
                  value={editForm.potongan}
                  onChange={(e) => setEditForm((f) => ({ ...f, potongan: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.status}
                    label="Status"
                    onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    <MenuItem value="menunggu">Menunggu</MenuItem>
                    <MenuItem value="diterima">Diterima</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Keterangan"
                  value={editForm.keterangan || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, keterangan: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Batal</Button>
          <Button variant="contained" onClick={handleUpdate}>Simpan</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MengelolaGajiKaryawan;
